import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon } from '@heroicons/react/16/solid';
import { EllipsisHorizontalIcon } from '@heroicons/react/16/solid';
import {
  LinkIcon,
  ArrowTopRightOnSquareIcon,
  TrashIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import {
  Dropdown,
  DropdownButton,
  DropdownMenu,
  DropdownItem,
} from '../../components_catalyst/dropdown';
import { Button } from '../../components_catalyst/button';
import { Toast } from '../../components_misc/Toast';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { AppDispatch } from '../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { deleteMilestoneMap } from '../../store/map3PendingSlice';
import { updateUser } from '../../store/map3PendingSlice';
import { RootState } from '../../store/store';

type DropdownMenuOption = 'copyLink' | 'star' | 'view' | 'delete';

/**
 * Based on basic card here: https://tailwindui.com/components/application-ui/layout/cards
 */
export function MapCard1({
  mapShortId,
  title,
  ownerName,
  dropdownMenuOptions,
}: {
  mapShortId: string;
  title: string;
  ownerName: string;
  dropdownMenuOptions?: DropdownMenuOption[];
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);

  // Find the map in either array to determine starred status
  const inSharedMilestoneMaps = userProfile.sharedMilestoneMaps?.find(
    (m) => m.shortId === mapShortId,
  );
  const inMyMilestoneMaps = userProfile.myMilestoneMaps?.find((m) => m.shortId === mapShortId);
  const isStarred = (inSharedMilestoneMaps || inMyMilestoneMaps)?.starred || false;

  const [showCopyNotification, setShowCopyNotification] = useState(false);

  // Default options include all actions if not specified
  const menuOptions = dropdownMenuOptions || ['copyLink', 'star', 'view', 'delete'];

  const handleCopyLink = () => {
    const url = `${window.location.origin}/${ownerName}/maps/${mapShortId}`;
    navigator.clipboard.writeText(url);
    setShowCopyNotification(true);
    setTimeout(() => setShowCopyNotification(false), 2000);
  };

  const handleView = () => {
    navigate(`/${ownerName}/maps/${mapShortId}`);
  };

  const handleDelete = async () => {
    try {
      await dispatch(deleteMilestoneMap(mapShortId)).unwrap();
    } catch (error) {
      console.error('Failed to delete map:', error);
    }
  };

  const handleStar = async () => {
    try {
      // Find the map in either array
      const inSharedMilestoneMaps = userProfile.sharedMilestoneMaps?.find(
        (m) => m.shortId === mapShortId,
      );
      const inMyMilestoneMaps = userProfile.myMilestoneMaps?.find((m) => m.shortId === mapShortId);
      const targetArray = inSharedMilestoneMaps ? 'sharedMilestoneMaps' : 'myMilestoneMaps';
      const currentMaps = inSharedMilestoneMaps
        ? userProfile.sharedMilestoneMaps
        : userProfile.myMilestoneMaps;
      const isStarred = (inSharedMilestoneMaps || inMyMilestoneMaps)?.starred || false;

      // Update the appropriate array
      const updatedMaps =
        currentMaps?.map((map) =>
          map.shortId === mapShortId ? { ...map, starred: !isStarred } : map,
        ) || [];

      await dispatch(
        updateUser({
          uid: userProfile.uid,
          updates: {
            [targetArray]: updatedMaps,
          },
        }),
      );
    } catch (error) {
      console.error('Failed to update star status:', error);
    }
  };

  return (
    <article
      className="overflow-hidden rounded-lg bg-white shadow"
      aria-label={mapShortId === '0' ? 'Create new map' : `Milestone map: ${title}`}
    >
      {mapShortId === '0' ? (
        <div
          className="flex items-center justify-center h-40 text-gray-200 bg-blue-600"
          role="button"
          aria-label="Create new milestone map"
        >
          <Link
            to={`/${ownerName}/maps/add`}
            title="Add a new milestone map"
            className="group relative"
          >
            <span className="sr-only">Add a new milestone map</span>
            <PlusIcon className="w-16 h-16" aria-hidden="true" />
          </Link>
        </div>
      ) : (
        <div className="relative">
          <Toast
            message={showCopyNotification ? 'Link copied to clipboard' : null}
            variant="info"
            position="center"
            onClose={() => setShowCopyNotification(false)}
          />
          <div className="absolute top-2 right-2" role="toolbar" aria-label="Map actions">
            <Dropdown>
              <DropdownButton as={Button} plain aria-label="Map options">
                <EllipsisHorizontalIcon className="h-5 w-5 text-zinc-500" aria-hidden="true" />
              </DropdownButton>
              <DropdownMenu aria-label="Map options menu">
                {menuOptions.includes('copyLink') && (
                  <DropdownItem onClick={handleCopyLink}>
                    <LinkIcon className="h-5 w-5" aria-hidden="true" />
                    Copy link to map
                  </DropdownItem>
                )}
                {menuOptions.includes('star') && (
                  <DropdownItem onClick={handleStar}>
                    <StarIcon
                      className={`h-5 w-5 ${
                        isStarred ? 'text-yellow-400 fill-current' : 'text-zinc-500'
                      }`}
                      aria-hidden="true"
                    />
                    {isStarred ? 'Unstar' : 'Star'}
                  </DropdownItem>
                )}
                {menuOptions.includes('view') && (
                  <DropdownItem onClick={handleView}>
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" aria-hidden="true" />
                    View
                  </DropdownItem>
                )}
                {menuOptions.includes('delete') && (
                  <DropdownItem onClick={handleDelete}>
                    <TrashIcon className="h-5 w-5" aria-hidden="true" />
                    Delete
                  </DropdownItem>
                )}
              </DropdownMenu>
            </Dropdown>
            <Button
              plain
              onClick={handleStar}
              aria-label={isStarred ? 'Remove star' : 'Star map'}
              aria-pressed={isStarred}
            >
              <StarIcon
                className={`h-5 w-5 ${
                  isStarred ? 'text-yellow-400 fill-current' : 'text-zinc-500'
                }`}
                aria-hidden="true"
              />
            </Button>
          </div>
          <div
            className="h-40 bg-gray-200 flex items-center justify-center"
            role="region"
            aria-label="Map preview"
          >
            <Link
              to={`/${ownerName}/maps/${mapShortId}`}
              aria-label={`Open ${title} milestone map`}
            >
              {title}
            </Link>
          </div>
        </div>
      )}
    </article>
  );
}
